import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserAuth } from "../context/AuthContext";
import { FaSearch, FaTimes, FaGlobe } from "react-icons/fa";
import { useContent } from "../context/ContentContext";
import { useTranslation } from "../hooks/useTranslation";
import { isLikelyFrench, translatePreservingFrench } from "../utils/lang";

const Navbar = () => {
  const { user, logOut } = UserAuth();
  const navigate = useNavigate();
  const { categories, series } = useContent();
  const { t, currentLanguage, setLanguage, translateText } = useTranslation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [translatedResults, setTranslatedResults] = useState([]);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate("/");
    } catch (error) {
      console.log(error);
    }
  };

  // Handle search with translation
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setSearchResults([]);
      setTranslatedResults([]);
      return;
    }

    const filteredResults = series
      .filter((s) => s.title.toLowerCase().includes(searchTerm.toLowerCase()))
      .map((s) => {
        const isLive = s.episodes?.some((ep) => ep.isLive) || false;
        return {
          ...s,
          isLive,
          categoryName: categories.find((c) => c.id === s.categoryId)?.name,
        };
      });

    setSearchResults(filteredResults);

    // Translate results if language is French
    if (currentLanguage === "fr" && filteredResults.length > 0) {
      translateSearchResults(filteredResults);
    } else {
      setTranslatedResults(filteredResults);
    }
  }, [searchTerm, series, categories, currentLanguage]);

  const translateSearchResults = async (results) => {
    try {
      const translated = await Promise.all(
        results.map(async (result) => {
          const title = result.title || "";
          const categoryName = result.categoryName || "";

          const translatedTitle =
            currentLanguage === "fr"
              ? await translatePreservingFrench(title, "fr", translateText)
              : title;

          const translatedCategoryName =
            currentLanguage === "fr" && categoryName
              ? await translatePreservingFrench(
                  categoryName,
                  "fr",
                  translateText
                )
              : categoryName;

          return { ...result, translatedTitle, translatedCategoryName };
        })
      );
      setTranslatedResults(translated);
    } catch (e) {
      console.error("Translation error:", e);
      setTranslatedResults(results);
    }
  };

  const handleResultClick = (seriesId) => {
    navigate(`/series/${seriesId}`);
    setSearchTerm("");
    setSearchResults([]);
    setTranslatedResults([]);
    setSearchOpen(false);
  };

  const toggleLanguage = () => {
    const newLanguage = currentLanguage === "en" ? "fr" : "en";
    setLanguage(newLanguage);
  };

  const displayResults =
    currentLanguage === "fr" ? translatedResults : searchResults;

  const tooltipText =
    currentLanguage === "fr"
      ? "Traduction automatique, exactitude non garantie"
      : "Auto translation, accuracy not guaranteed";

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between p-4 z-[100] w-full absolute">
      <Link to="/">
        <img
          className="w-30 h-16 sm:h-20 md:h-20 object-cover"
          src={`/logo.png?${new Date().getTime()}`}
          alt="Logo"
          onError={(e) => {
            console.error("Failed to load image:", e.target.src);
            e.target.src = "/path/to/placeholder.png";
          }}
        />
      </Link>

      <div className="flex items-center gap-4">
        {/* Language Switcher Button */}
        <button
          onClick={toggleLanguage}
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg transition-colors duration-200"
          title={tooltipText}
          aria-label={tooltipText}
        >
          <FaGlobe className="text-sm" />
          <span className="text-sm font-medium">
            {currentLanguage === "en" ? "FR" : "EN"}
          </span>
        </button>

        {/* Search Section */}
        {/*         <div className={`relative flex items-center ${searchOpen ? "mt-4 sm:mt-0" : ""}`}>
          {!searchOpen ? (
            <FaSearch
              className="text-white cursor-pointer hover:text-gray-300 transition-colors"
              onClick={() => setSearchOpen(true)}
              title={t('search')}
            />
          ) : (
            <div className="relative flex items-center">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="p-2 w-48 sm:w-64 border border-gray-300 rounded pl-10"
                placeholder={t('searchPlaceholder')}
                autoFocus
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer" />
              <FaTimes
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600"
                onClick={() => {
                  setSearchOpen(false);
                  setSearchTerm("");
                  setSearchResults([]);
                  setTranslatedResults([]);
                }}
              />
              
              {displayResults.length > 0 && (
                <ul className="bg-white border border-gray-300 rounded-b max-h-64 overflow-y-auto absolute top-12 left-0 w-48 sm:w-64 shadow-lg z-50">
                  {displayResults.map((result) => (
                    <li
                      key={result.id}
                      className="p-2 cursor-pointer hover:bg-gray-200 flex justify-between items-center relative"
                      onClick={() => handleResultClick(result.id)}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {currentLanguage === 'fr' && result.translatedTitle 
                            ? result.translatedTitle 
                            : result.title}
                        </span>
                        {result.categoryName && (
                          <span className="text-xs text-gray-500">
                            {currentLanguage === 'fr' && result.translatedCategoryName 
                              ? result.translatedCategoryName 
                              : result.categoryName}
                          </span>
                        )}
                      </div>
                      {result.isLive && (
                        <span className="bg-red-600 text-white text-xs px-2 py-1 rounded">
                          {t('live')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div> */}

        {/* Account Section - Enable when needed */}
        {/*         <div className="text-right ml-2 sm:ml-4">
          {user?.email ? (
            <>
              <Link to="/account">
                <button className="text-white py-2 pr-2 sm:pr-4">
                  {t('account')}
                </button>
              </Link>
              <button
                onClick={handleLogout}
                className="bg-black px-4 sm:px-6 py-2 rounded cursor-pointer text-white"
              >
                {t('logout')}
              </button>
            </>
          ) : (
            <>
              <Link to="/login">
                <button className="text-white pr-2 sm:pr-4">{t('signIn')}</button>
              </Link>
              <Link to="/signup">
                <button className="bg-black px-4 sm:px-6 py-2 rounded cursor-pointer text-white">
                  {t('signUp')}
                </button>
              </Link>
            </>
          )}
        </div> */}
      </div>
    </div>
  );
};

export default Navbar;
